import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Flex,
  Input,
  Text,
  Heading,
  List,
  ListItem,
  IconButton,
  Button,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Select,
  Tooltip,
  Textarea
} from '@chakra-ui/react';
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import {
  SendHorizonal, UserRound, ArrowLeft, Bold, Italic, Underline, Download, List as ListIcon,
  ListOrdered as ListOrderedIcon, CornerDownLeft
} from 'lucide-react';
import { AttachmentIcon } from '@chakra-ui/icons';
import ReusableInput from 'components/Inputs/ReusableInput';
import NoDataFound from 'components/NoDataGraphics/NoDataFound';
import { fetchUsersAction } from '../../../state/actions/userActions';
import {
  createChatAction,
  getChatBetweenUsersAction,
  getAllChatsAction,
  markChatAsReadAction,
} from '../../../state/actions/chatActions';
import { useLocation } from 'react-router-dom';
import ReusableDrawer from 'components/Drawers/ReusableDrawer';
import ViewProfile from '../UserManagement/components/ViewProfile';
import DocumentIcon from 'components/DocumentViewer/DocumentIcon';
import DocumentViewer from 'components/DocumentViewer/DocumentViewer';

const headerBgImage = require('../../../assets/img/backarrow.jpg');
const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/`.replace(/\/+$/, "/");

const Chat = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [messageCounts, setMessageCounts] = useState({});
  const [unreadCounts, setUnreadCounts] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [isViewProfileDrawerOpen, setViewProfileDrawerOpen] = useState(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const preselectedUserId = queryParams.get("userId");
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isDocModalOpen, setDocModalOpen] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const dispatch = useDispatch();
  const messagesEndRef = useRef(null);

  const userId =
    useSelector((state) => state.auth?.user?.user?.user_id) ||
    useSelector((state) => state.auth?.user?.data?.user_id);

  const { users } = useSelector((state) => state.users);

  const {
    chat,
    allChats,
    loadingChatBetween,
  } = useSelector((state) => state.chat);

  useEffect(() => {
    dispatch(fetchUsersAction());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllChatsAction());
  }, [dispatch]);


  const openDocumentModal = (doc) => {
    setSelectedDocument({
      url: doc.url,
      type: doc.name?.split(".").pop(),
      title: doc.name,
    });
    setDocModalOpen(true);
  };

  const closeDocumentModal = () => {
    setSelectedDocument(null);
    setDocModalOpen(false);
  };

  useEffect(() => {
    if (preselectedUserId && users?.length > 0) {
      const foundUser = users.find(
        (u) => u.user_id === preselectedUserId
      );

      if (foundUser) {
        handleSelectContact(foundUser);
      }
    }
  }, [preselectedUserId, users]);

  const formatMessages = useCallback((chatData, contact) => {
    if (!chatData || !contact) return [];

    return chatData.map((chatItem) => ({
      id: chatItem.chat_id,
      text: chatItem.message,
      sender: chatItem.sender_id === userId ? 'You' : contact?.username,
      createdAt: chatItem.created_at,
      isYou: chatItem.sender_id === userId,
      chatType: chatItem.chat_type,
      receiverId: chatItem.receiver_id,
      senderId: chatItem.sender_id,
      updatedAt: chatItem.updated_at,
      status: chatItem.status,
      images: (chatItem.documents || [])
        .filter((doc) => doc.document_type === "Chat Image")
        .map((doc) => ({
          url: doc.file_path
            ? BASE_URL + doc.file_path.replace(/^\//, '')
            : doc.uri,
          name: doc.file_name || doc.name,
        })),
      documents: (chatItem.documents || [])
        .filter((doc) => doc.document_type !== "Chat Image")
        .map((doc) => ({
          url: doc.file_path
            ? BASE_URL + doc.file_path.replace(/^\//, '')
            : doc.uri,
          name: doc.file_name || doc.name || "Document",
          type: doc.file_extension || "",
          document_type: doc.document_type,
        })),
    }));
  }, [userId]);

  useEffect(() => {
    if (selectedContact) {
      dispatch(getChatBetweenUsersAction(selectedContact.user_id));
    }
  }, [dispatch, selectedContact]);


  const applyFormat = (type) => {
    const textarea = document.getElementById("chat-textarea");
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    const selectedText = inputValue.substring(start, end);

    let formatted = selectedText;

    if (type === "bold") formatted = `**${selectedText}**`;
    if (type === "italic") formatted = `*${selectedText}*`;
    if (type === "underline") formatted = `__${selectedText}__`;

    if (type === "bullet") {
      formatted = selectedText
        .split("\n")
        .map(line => `• ${line}`)
        .join("\n");
    }

    if (type === "number") {
      formatted = selectedText
        .split("\n")
        .map((line, index) => `${index + 1}. ${line}`)
        .join("\n");
    }

    const newText =
      inputValue.substring(0, start) +
      formatted +
      inputValue.substring(end);

    setInputValue(newText);
  };

  const insertNewLine = () => {
    const textarea = document.getElementById("chat-textarea");

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    const newText =
      inputValue.substring(0, start) +
      "\n" +
      inputValue.substring(end);

    setInputValue(newText);
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + 1;
      textarea.focus();
    }, 0);
  };

  useEffect(() => {
    if (!selectedContact || !Array.isArray(chat)) return;
    const formattedMessages = formatMessages(chat, selectedContact);
    setMessages(formattedMessages);
  }, [chat, selectedContact, formatMessages]);

  useEffect(() => {
    if (!userId || !Array.isArray(users)) return;

    const chatsArray = Array.isArray(allChats) ? allChats : [];
    const counts = {};
    const unread = {};

    users.forEach((u) => {
      counts[u.user_id] = 0;
      unread[u.user_id] = 0;
    });

    for (const item of chatsArray) {
      const chatItem = item?.chat || item;

      const senderId = chatItem?.sender_id;
      const receiverId = chatItem?.receiver_id;
      const status = chatItem?.status;

      const involvesMe = senderId === userId || receiverId === userId;
      if (!involvesMe) continue;

      const otherUserId = senderId === userId ? receiverId : senderId;
      if (!otherUserId) continue;

      counts[otherUserId] = (counts[otherUserId] || 0) + 1;

      if (
        receiverId === userId &&
        senderId !== userId &&
        status === 'sent'
      ) {
        unread[otherUserId] = (unread[otherUserId] || 0) + 1;
      }
    }

    setMessageCounts(counts);
    setUnreadCounts(unread);
  }, [allChats, users, userId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const userData = Array.isArray(users) ? users : [];
  const categories = ['All', 'Individual', 'Business', 'Driver', 'Agent', 'Admin'];

  const categoryMessageCounts = categories.reduce((acc, category) => {
    if (category === 'All') {
      acc[category] = Object.values(unreadCounts).reduce((a, b) => a + b, 0);
    } else {
      const filteredUsers = userData.filter((user) => user.user_type === category);
      acc[category] = filteredUsers.reduce((total, user) => {
        return total + (unreadCounts[user.user_id] || 0);
      }, 0);
    }
    return acc;
  }, {});

  const filteredContacts = React.useMemo(() => {
    if (!Array.isArray(users)) return [];

    const chatsArray = Array.isArray(allChats) ? allChats : [];

    const getLatestMessageTime = (contactUserId) => {
      const relatedChats = chatsArray.filter((item) => {
        const chatItem = item?.chat || item;

        return (
          (chatItem?.sender_id === userId && chatItem?.receiver_id === contactUserId) ||
          (chatItem?.receiver_id === userId && chatItem?.sender_id === contactUserId)
        );
      });

      if (!relatedChats.length) return 0;

      return relatedChats.reduce((latest, current) => {
        const chatItem = current?.chat || current;
        const time = new Date(
          chatItem?.updated_at || chatItem?.created_at || 0
        ).getTime();

        return time > latest ? time : latest;
      }, 0);
    };

    return users
      .filter((user) => {
        const username =
          user.username || `${user.first_name || ''} ${user.last_name || ''}`.trim();

        const matchesCategory =
          activeCategory === 'All' || user.user_type === activeCategory;

        const matchesSearchQuery = username
          .toLowerCase()
          .includes(searchQuery.toLowerCase());

        const hasMessages = (messageCounts[user.user_id] || 0) > 0;

        return hasMessages && matchesCategory && matchesSearchQuery;
      })
      .sort((a, b) => {
        const timeA = getLatestMessageTime(a.user_id);
        const timeB = getLatestMessageTime(b.user_id);

        return timeB - timeA;
      });
  }, [users, allChats, userId, activeCategory, searchQuery, messageCounts]);

  const handleSendMessage = async () => {
    if (!selectedContact) return;
    if (!inputValue.trim() && attachments.length === 0) return;

    const timestamp = new Date().toISOString();
    const tempMessages = [];

    attachments.forEach((file) => {
      tempMessages.push({
        id: Date.now().toString() + Math.random(),
        senderId: userId,
        receiverId: selectedContact.user_id,
        status: "sending",
        createdAt: timestamp,
        type: file.type?.startsWith("image/") ? "image" : "document",
        preview: file.preview,
        fileObject: file.rawFile,
        name: file.name,
        images: file.type?.startsWith("image/")
          ? [{ url: file.preview, name: file.name }]
          : [],
        documents: !file.type?.startsWith("image/")
          ? [{ url: file.preview, name: file.name, document_type: file.type }]
          : [],
        isYou: true,
      });
    });

    if (inputValue.trim()) {
      tempMessages.push({
        id: Date.now().toString(),
        senderId: userId,
        receiverId: selectedContact.user_id,
        status: "sending",
        createdAt: timestamp,
        type: "text",
        text: inputValue.trim(),
        images: [],
        documents: [],
        isYou: true,
      });
    }

    setMessages((prev) => [...prev, ...tempMessages]);

    const messageText = inputValue.trim();
    setInputValue("");
    setAttachments([]);

    const chatData = {
      sender_id: userId,
      receiver_id: selectedContact.user_id,
      chat_type: "Admin",
      message: messageText || null,
      images: tempMessages
        .filter((m) => m.type === "image")
        .map((m) => m.fileObject),
      documents: tempMessages
        .filter((m) => m.type === "document")
        .map((m) => m.fileObject),
    };

    try {
      const response = await dispatch(createChatAction(chatData));

      if (response?.success) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.status === "sending"
              ? { ...msg, status: "sent" }
              : msg
          )
        );
      } else {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.status === "sending" ? { ...msg, status: "failed" } : msg
          )
        );
        alert(response?.error || "Failed to send message");
      }
    } catch (err) {
      console.error(err);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.status === "sending"
            ? { ...msg, status: "failed" }
            : msg
        )
      );
      alert("Failed to send message");
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    const newAttachments = files.map((file) => ({
      name: file.name,
      type: file.type,
      rawFile: file,
      preview: URL.createObjectURL(file),
    }));
    setAttachments([...attachments, ...newAttachments]);
  };

  const handleSelectContact = (contact) => {
    setSelectedContact(contact);
    setMessages([]);
    setUnreadCounts((prev) => ({ ...prev, [contact.user_id]: 0 }));

    const chatsArray = Array.isArray(allChats) ? allChats : [];

    chatsArray.forEach((item) => {
      const chatItem = item?.chat || item;

      const isUnreadForMe =
        chatItem?.sender_id === contact.user_id &&
        chatItem?.receiver_id === userId &&
        chatItem?.status === "sent";

      if (isUnreadForMe) {
        dispatch(markChatAsReadAction(chatItem.chat_id));
      }
    });
  };

  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
    onOpen();
  };

  const sortedMessages = [...messages].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  );

  const handleViewProfile = (user) => {
    setSelectedUserProfile(user);
    setViewProfileDrawerOpen(true);
  };

  const handleCloseViewProfileDrawer = () => {
    setSelectedUserProfile(null);
    setViewProfileDrawerOpen(false);
  };

  if (loadingChatBetween) {
    return (
      <Flex maxW="1000px" mx="auto" my="4" mt={20} flexDirection="column" gap={4}>
        <Box><Skeleton height="40px" width="100%" /></Box>
        <Flex direction={{ base: 'column', md: 'row' }}>
          <Box width={{ base: '100%', md: '30%' }} p="4">
            <Skeleton height="30px" width="80%" mb={4} />
            <Skeleton height="40px" width="100%" mb={2} />
            {Array(5).fill(0).map((_, i) => (
              <Skeleton key={i} height="50px" width="100%" mb={2} />
            ))}
          </Box>
          <Box flex="1" p="4" ml={{ base: 0, md: '4' }} display="flex" flexDirection="column" gap={4}>
            <Skeleton height="30px" width="50%" />
            <Box flex="1">
              {Array(6).fill(0).map((_, i) => (
                <Skeleton key={i} height="40px" width="100%" mb={2} />
              ))}
            </Box>
            <Flex gap={2}>
              <Skeleton height="40px" width="100%" flex="1" />
              <Skeleton height="40px" width="40px" />
              <Skeleton height="40px" width="80px" />
            </Flex>
          </Box>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex
      maxW="1000px"
      mx="auto"
      my="4"
      mt={10}
      px={{ base: 4, md: 0 }}
      borderRadius="md"
      boxShadow="md"
      flexDirection="column"
    >
      <Box border="1px" borderColor="gray.200" p="4" borderRadius="md" mb="4">
        <Box display={{ base: 'block', md: 'none' }} mb={3}>
          <Select
            value={activeCategory}
            onChange={(e) => setActiveCategory(e.target.value)}
            borderColor="#071D54"
            color="#071D54"
            bg="white"
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
                {categoryMessageCounts[category] > 0
                  ? ` (${categoryMessageCounts[category]})`
                  : ''}
              </option>
            ))}
          </Select>
        </Box>

        <Flex
          justify="start"
          ml={2}
          flexWrap="wrap"
          gap={2}
          display={{ base: 'none', md: 'flex' }}
        >
          {categories.map((category) => (
            <Button
              key={category}
              onClick={() => setActiveCategory(category)}
              bg={activeCategory === category ? '#071D54' : 'transparent'}
              color={activeCategory === category ? 'white' : '#071D54'}
              _hover={{ bg: '#071D54', color: 'white' }}
              borderRadius="12px"
              border="1px solid #071D54"
              px={6}
              display="flex"
              alignItems="center"
              gap={2}
            >
              <Text>{category}</Text>
              {categoryMessageCounts[category] > 0 && (
                <Box
                  bg="red.500"
                  color="white"
                  borderRadius="full"
                  minW="20px"
                  h="20px"
                  aspectRatio={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="xs"
                >
                  {categoryMessageCounts[category]}
                </Box>
              )}
            </Button>
          ))}
        </Flex>
      </Box>

      <Flex direction={{ base: 'column', md: 'row' }}>
        <Box
          width={{ base: '100%', md: '30%' }}
          p="4"
          bgImage={`url(${headerBgImage})`}
          bgSize="cover"
          bgPosition="center"
          color="white"
          borderRadius="md"
          overflowY="auto"
          maxHeight={{ base: selectedContact ? '220px' : '500px', md: '500px' }}
          display={{ base: selectedContact ? 'none' : 'block', md: 'block' }}
        >
          <Heading size="md" mb="4">Chats</Heading>
          <Input
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg="gray.100"
            color="black"
            mb="4"
          />

          {filteredContacts.length === 0 ? (
            <NoDataFound
              message="No chats found"
              textColor={"white"}
              height={"90px"}
              width={"90px"}
            />
          ) : (
            <List spacing={2}>
              {filteredContacts.map((contact) => {
                const unread = unreadCounts[contact.user_id] || 0;
                const total = messageCounts[contact.user_id] || 0;
                const badgeValue = unread > 0 ? unread : 0;

                return (
                  <ListItem
                    key={contact.user_id}
                    cursor="pointer"
                    onClick={() => handleSelectContact(contact)}
                    bg={
                      selectedContact?.user_id === contact.user_id
                        ? '#1E3A8A'
                        : 'transparent'
                    }
                    p="2"
                    borderRadius="md"
                    _hover={{ bg: '#1E3A8A' }}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Flex align="center" gap={2} minW={0}>
                      <Icon
                        as={UserRound}
                        boxSize={7}
                        color="white"
                        bg="#071D54"
                        borderRadius="full"
                        p={1}
                        stroke="none"
                        fill="currentColor"
                        flexShrink={0}
                      />
                      <Box minW={0}>
                        <Text noOfLines={1}>
                          {contact.username ||
                            `${contact.first_name || ''} ${contact.last_name || ''}`.trim()}
                        </Text>
                        {total > 0 && (
                          <Text fontSize="xs" color="gray.300">
                            {total} message{total !== 1 ? 's' : ''}
                          </Text>
                        )}
                      </Box>
                    </Flex>

                    {badgeValue > 0 && (
                      <Box
                        bg="red.500"
                        color="white"
                        borderRadius="full"
                        minW="20px"
                        h="20px"
                        aspectRatio={1}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontSize="xs"
                        ml={2}
                        flexShrink={0}
                      >
                        {badgeValue}
                      </Box>
                    )}
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>

        <Box
          flex="1"
          p="4"
          borderRadius="md"
          bg="white"
          ml={{ base: 0, md: '4' }}
          mt={{ base: 4, md: 0 }}
          display="flex"
          flexDirection="column"
          maxHeight="500px"
          minH={{ base: '60vh', md: 'unset' }}
        >
          <Flex align="center" gap={2} mb="4">
            {selectedContact && (
              <IconButton
                display={{ base: 'inline-flex', md: 'none' }}
                aria-label="Back to chats"
                icon={<ArrowLeft size={18} />}
                variant="ghost"
                onClick={() => setSelectedContact(null)}
              />
            )}

            {selectedContact && (
              <Tooltip
                label="View Profile"
                placement="top"
                hasArrow
                bg="#071D54"
                color="white"
              >
                <Box
                  cursor="pointer"
                  onClick={() => handleViewProfile(selectedContact)}
                >
                  <Icon
                    as={UserRound}
                    boxSize={7}
                    color="white"
                    bg="#071D54"
                    borderRadius="full"
                    p={1}
                    stroke="none"
                    fill="currentColor"
                  />
                </Box>
              </Tooltip>
            )}

            <Heading size="md" noOfLines={1}>
              {selectedContact
                ? `Chat with ${selectedContact.username || `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim()}`
                : 'Select a contact'}
            </Heading>
          </Flex>

          <Box
            flex="1"
            overflowY="auto"
            mb="4"
            border="1px"
            borderColor="gray.200"
            borderRadius="md"
            p="2"
          >
            {sortedMessages.length > 0 ? (
              <>
                {sortedMessages.map((msg) => (
                  <Flex
                    key={msg.id}
                    mb="2"
                    justify={msg.isYou ? 'flex-end' : 'flex-start'}
                  >
                    {!msg.isYou && (
                      <Tooltip
                        label="View Profile"
                        placement="top"
                        hasArrow
                        bg="#071D54"
                        color="white"
                      >
                        <Box
                          cursor="pointer"
                          onClick={() => handleViewProfile(selectedContact)}
                          mr="2"
                          display={{ base: 'none', sm: 'inline-flex' }}
                        >
                          <Icon
                            as={UserRound}
                            boxSize={7}
                            color="white"
                            bg="#071D54"
                            borderRadius="full"
                            p={1}
                            stroke="none"
                            fill="currentColor"
                          />
                        </Box>
                      </Tooltip>
                    )}

                    <Box
                      p="3"
                      borderRadius="md"
                      bg={msg.isYou ? '#1E3A8A' : '#F0F0F0'}
                      color={msg.isYou ? 'white' : 'black'}
                      maxW={{ base: '90%', md: '70%' }}
                      wordBreak="break-word"
                    >
                      <Text
                        whiteSpace="pre-wrap"
                        dangerouslySetInnerHTML={{
                          __html: (msg.text ?? msg.message ?? "")
                            .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>")
                            .replace(/\*(.*?)\*/g, "<i>$1</i>")
                            .replace(/__(.*?)__/g, "<u>$1</u>")
                        }}
                      />

                      {(msg.images || []).map((img, i) => (
                        <Box
                          key={i}
                          borderRadius="md"
                          overflow="hidden"
                          maxW="150px"
                          mt={2}
                          cursor="pointer"
                        >
                          <img
                            src={img.url}
                            alt={img.name || 'image'}
                            style={{ width: '100%', borderRadius: '6px' }}
                            onClick={() => openImageModal(img.url)}
                          />
                        </Box>
                      ))}

                      {/* DOCUMENTS */}
                      {(msg.documents || [])
                        .filter((doc) => doc.document_type !== "Chat Image")
                        .map((doc, i) => {
                          const extension = doc.name?.split(".").pop()?.toLowerCase();

                          return (
                            <Box
                              key={i}
                              mt={2}
                              p={2}
                              borderRadius="md"
                              display="flex"
                              alignItems="center"
                              gap={3}
                              cursor="pointer"
                              onClick={() => openDocumentModal(doc)}
                            >
                              <DocumentIcon type={extension} />

                              <Box>
                                <Text fontSize="sm" fontWeight="600">
                                  {doc.name || "Document"}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  Click to preview
                                </Text>
                              </Box>
                            </Box>
                          );
                        })}

                      <Text
                        fontSize="xs"
                        color={msg.isYou ? 'gray.300' : 'gray.500'}
                        mt={1}
                      >
                        {new Date(msg.createdAt).toLocaleDateString([], {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}

                        {" • "}

                        {new Date(msg.createdAt).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}

                        {" , "}

                        {msg.status === "sending" && "Sending..."}
                        {msg.status === "sent" && "Sent"}
                        {msg.status === "read" && "Read"}
                        {msg.status === "failed" && "Failed"}
                      </Text>

                      {msg.status === "failed" && (
                        <Text
                          fontSize="xs"
                          color="red.400"
                          cursor="pointer"
                          mt={1}
                          onClick={() => retrySend(msg)}
                        >
                          Tap to retry
                        </Text>
                      )}
                    </Box>

                    {msg.isYou && (
                      <Icon
                        as={UserRound}
                        boxSize={7}
                        color="white"
                        bg="#071D54"
                        borderRadius="full"
                        p={1}
                        stroke="none"
                        fill="currentColor"
                        ml="2"
                        display={{ base: 'none', sm: 'inline-flex' }}
                      />
                    )}
                  </Flex>
                ))}
                <div ref={messagesEndRef} />
              </>
            ) : (
              <Flex
                height="100%"
                align="center"
                justify="center"
                textAlign="center"
                mt={10}
              >
                <Text color="gray.500">
                  {selectedContact
                    ? "No messages yet. Start the conversation!"
                    : "Select a contact to start chatting"}
                </Text>
              </Flex>
            )}
          </Box>

          {selectedContact && (
            <Flex
              direction={{ base: 'column', sm: 'row' }}
              gap={2}
            >
              <Box flex="1">

                {attachments.length > 0 && (
                  <Flex wrap="wrap" gap={2} mb={2}>
                    {attachments.map((file, i) => {
                      const isImage = file.type?.startsWith("image/");

                      return (
                        <Box key={i} position="relative">
                          {isImage ? (
                            <img
                              src={file.preview}
                              style={{ width: 80, height: 80, borderRadius: 6 }}
                            />
                          ) : (
                            <Box
                              p={2}
                              border="1px solid #E2E8F0"
                              borderRadius="md"
                              display="flex"
                              alignItems="center"
                              gap={2}
                              w="120px"
                            >
                              <DocumentIcon type={file.name.split(".").pop()} />
                              <Text fontSize="xs" noOfLines={1}>
                                {file.name}
                              </Text>
                            </Box>
                          )}

                          {/* REMOVE BUTTON */}
                          <Box
                            position="absolute"
                            top="-5px"
                            right="-5px"
                            bg="red.500"
                            color="white"
                            borderRadius="full"
                            w="18px"
                            h="18px"
                            fontSize="xs"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            cursor="pointer"
                            onClick={() =>
                              setAttachments((prev) =>
                                prev.filter((_, index) => index !== i)
                              )
                            }
                          >
                            ×
                          </Box>
                        </Box>
                      );
                    })}
                  </Flex>
                )}

                {/* Toolbar */}
                <Flex mb={1} gap={1}>
                  <IconButton size="sm" icon={<Bold size={14} />} onClick={() => applyFormat("bold")} />
                  <IconButton size="sm" icon={<Italic size={14} />} onClick={() => applyFormat("italic")} />
                  <IconButton size="sm" icon={<Underline size={14} />} onClick={() => applyFormat("underline")} />
                  <IconButton size="sm" icon={<ListIcon size={14} />} onClick={() => applyFormat("bullet")} />
                  <IconButton size="sm" icon={<ListOrderedIcon size={14} />} onClick={() => applyFormat("number")} />
                  <IconButton size="sm" icon={<CornerDownLeft size={14} />} onClick={insertNewLine} aria-label="New Line" />
                </Flex>

                {/* TEXT INPUT */}
                <Textarea
                  id="chat-textarea"
                  placeholder="Type your message..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  resize="vertical"
                  rows={3}
                  bg="gray.100"
                />
              </Box>
              <input
                type="file"
                id="file-upload"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />

              <Flex
                gap={2}
                justify={{ base: 'flex-end', sm: 'initial' }}
                align="flex-end">
                <IconButton
                  as="label"
                  htmlFor="file-upload"
                  aria-label="Attach files"
                  icon={<AttachmentIcon color="#1E3A8A" />}
                  variant="ghost"
                />
                <IconButton
                  aria-label="Send message"
                  icon={<SendHorizonal size={18} />}
                  onClick={handleSendMessage}
                  isDisabled={!inputValue.trim() && attachments.length === 0}
                  bg="#071D54"
                  color="white"
                  solid
                  borderRadius="full"
                  _hover={{ bg: '#052050' }}
                />
              </Flex>
            </Flex>
          )}
        </Box>
      </Flex>
      <ReusableDrawer
        isOpen={isViewProfileDrawerOpen}
        onClose={handleCloseViewProfileDrawer}
        headerContent={<h2 style={{ fontSize: "md" }}>View Profile</h2>}
        bodyContent={
          selectedUserProfile && (
            <ViewProfile
              user={selectedUserProfile}
              onClose={handleCloseViewProfileDrawer}
            />
          )
        }
      />
      <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'xl' }} isCentered>
        <ModalOverlay />
        <ModalContent bg="transparent" boxShadow="none">
          <ModalCloseButton color="white" />
          <ModalBody p={0}>
            <img
              src={selectedImage}
              alt="Preview"
              style={{ width: '100%', borderRadius: '8px' }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
      <Modal isOpen={isDocModalOpen} onClose={closeDocumentModal} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent maxH="80vh">
          <ModalCloseButton />

          <ModalBody p={2}
            flex="1"
            overflowY="auto">
            {selectedDocument ? (
              <>
                <DocumentViewer documents={[selectedDocument]} />

                <IconButton
                  mt={4}
                  icon={<Download />}
                  onClick={() =>
                    window.open(selectedDocument.url, "_blank")
                  }
                />
              </>
            ) : (
              <Text>No document</Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default Chat;